import React, {useContext, useState, FormEvent} from 'react';
import {useNavigate} from 'react-router-dom';
import { titleCase } from 'title-case';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import Copyright from '../Copyright';
import {landingImage} from '../../assets';
import RootContext from '../../services/context-states/root-context';
import AuthService from '../../services/auth';
import {audience, setResetApiAuth, userApi} from '../../api/openapi-axios-client';
import { LoginFormData, UserSession } from '../../types/auth';
import { Session } from '../../types/api-client-types';
import { callApi } from '../../api/helpers';
import { LoginSocialGoogle, IResolveParams } from 'reactjs-social-login';
import GoogleSvg from './social-third-party-login/google/GoogleSvg';
import MicrosoftSvg from './social-third-party-login/microsoft/MicrosoftSvg';
import { LoginMicrosoft } from './social-third-party-login/microsoft/LoginMicrosoft';
import ThirdPartyLoginButton from './social-third-party-login/ThirdPartyLoginButton';
import { HostedDivider } from '../../elements';

const Login: React.FC = () => {
  const {tokenContext, userContext} = useContext(RootContext);
  const {setToken} = tokenContext;
  const {setCurrentUser} = userContext;
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const redirectUri = `${window.location.origin}`;
    const redirectMicrosoftUri = `${window.location.origin}/auth-redirect`

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    setError('');
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const submissionsData: LoginFormData = {
      email: formData.get('email') as string,
      password: formData.get('password') as string,
    };

    await handleLogin('futuregen', submissionsData);
  };

  const handleThirdPartyLoginSuccess = async (data: IResolveParams) => {
    const { access_token, email } = data.data as {
      access_token?: string;
      email?: string;
  };

  if (!access_token || !email) {
      setError(`Error logging in with ${titleCase(data.provider)}`);
      return;
  }

  await handleLogin(data.provider, {
    email,
    password: access_token
  });
  }

  const handleThirdPartyLoginError = (error: any) => {
    const {type, message} = error as {type: string, message: string};

    if(type !== 'popup_closed') {
        return;
    }

    setError(message);
  }

  const handleLogin = async (loginProvider: string, loginData: LoginFormData) => {
    try {
      const data = await callApi<Session>(() => userApi.account.authorize(
        {
          email: loginData.email,
          password: loginData.password,
          audience,
          loginProvider
        }, 
        'user'
      ));

      if (data?.token) {
        const sessionData: UserSession = {
          ...data,
          expiry: new Date().getTime() + 5000,
          SessionAudience: audience
        };

        localStorage.setItem('user', JSON.stringify(sessionData));

        setToken(data.token);

        const decodedToken : UserSession | null = AuthService.decodeUserToken(data.token);

        if (decodedToken?.userData) {
            setCurrentUser(decodedToken.userData);
            setResetApiAuth();
            navigate('/home');
        }

      } else {
        setError('login unsuccessful, please try again');
      }
    } catch (err) {
      setError((err as Error).message || 'invalid username or password');
    }
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 5, md: 10 }, px: { xs: 2, sm: 3 } }}>
      <Grid container direction='row' justifyContent="space-evenly" spacing={{ xs: 0, md: 5 }}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 1, md: 2 }, display: { xs: 'none', md: 'block' } }}>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <img
              src={landingImage}
              alt="Login illustration"
              style={{
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 8,
              }}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 2, md: 1 } }}>
          <Paper
            elevation={isMobile ? 0 : 3}
            sx={{
              p: { xs: 3, sm: 4 },
              backgroundColor: { xs: 'transparent', md: 'background.paper' },
              borderRadius: 2,
            }}
          >
            {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}

            <Typography component='h5' variant='h5' sx={{
              fontWeight: 900,
              fontSize: { xs: 24, sm: 28, md: 32 },
              textAlign: { xs: 'center', md: 'left' }
            }}>
              Welcome Back!
            </Typography>
            <Typography component='p' variant='body1' sx={{
              fontSize: { xs: 14, sm: 16 },
              textAlign: { xs: 'center', md: 'left' },
              mb: 3
            }}>
              Log into your Future Gen account.
            </Typography>
            <Box component='form' onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <TextField
                margin='normal'
                required
                fullWidth
                id='email'
                label='Email'
                name='email'
                autoComplete='email'
                autoFocus={!isMobile}
                sx={{ mb: 2 }}
              />
              <TextField
                margin='normal'
                required
                fullWidth
                name='password'
                label='Password'
                type={showPassword ? 'text' : 'password'}
                id='password'
                autoComplete='current-password'
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={<Checkbox value='remember' color='primary' />}
                label='Remember me'
                sx={{ mb: 2 }}
              />
              <Button
                type='submit'
                fullWidth
                variant='contained'
                sx={{
                  mt: 2,
                  mb: 2,
                  height: { xs: 48, sm: 56 },
                  fontSize: { xs: 16, sm: 18 },
                }}>
                Sign In
              </Button>
            </Box>
            <Grid container sx={{ justifyContent: 'space-between', mb: 3 }}>
              <Grid>
                <Link
                  component='button'
                  variant='body2'
                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                    event.preventDefault();
                    navigate('/reset-password');
                  }}
                  sx={{ fontSize: { xs: 13, sm: 14 } }}>
                  Forgot password?
                </Link>
              </Grid>
              <Grid>
                <Link
                  component='button'
                  variant='body2'
                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                    event.preventDefault();
                    navigate('/register');
                  }}
                  sx={{ fontSize: { xs: 13, sm: 14 } }}
                >
                  Don't have an account? Sign Up
                </Link>
              </Grid>
            </Grid>

            <HostedDivider style={{ marginTop: 20, marginBottom: 20 }}>
              <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: { xs: 12, sm: 14 } }}>
                OR
              </Typography>
            </HostedDivider>

            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 2, sm: 3 },
              mt: 2,
              mb: 2
            }}>
            <LoginSocialGoogle
              client_id={process.env.GOOGLE_CLIENT_ID || ''}
              redirect_uri={redirectUri}
              scope='openid profile email'
              fetch_basic_profile={true}
              isOnlyGetToken={false}
              onResolve={async (data: IResolveParams) => await handleThirdPartyLoginSuccess(data)}
              onReject={error => handleThirdPartyLoginError(error)}>
              <ThirdPartyLoginButton mode={theme.palette.mode} type='login' logo={<GoogleSvg />} text='Login with Google' aria-label="Login with Google account" />
            </LoginSocialGoogle>
            <LoginMicrosoft
                        client_id={process.env.MICROSOFT_CLIENT_ID || ''}
                        redirect_uri={redirectMicrosoftUri}
                        onResolve={(data: IResolveParams) => handleThirdPartyLoginSuccess(data)}
                        onReject={error => handleThirdPartyLoginError(error)}>
              <ThirdPartyLoginButton mode={theme.palette.mode} type='login' logo={<MicrosoftSvg />} text='Login with Microsoft' aria-label="Login with Microsoft account" />
            </LoginMicrosoft>
            </Box>

            <Copyright sx={{ mt: 3 }} />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Login; 